import axios from 'axios'
import 'url-search-params-polyfill'
import setCruiseTours from './setCruiseTours'
import triggerCustomGaEvent from '../utilities/triggerCustomGaEvent'
import formatVPSPackages from '../utilities/formatVPSPackages'
import { setLandPackages } from './setLandPackages'
import { setActivePagination } from './setActiveSearchFilters'
import formatLandPackages from '../utilities/formatLandPackages'
import { formatVPSCruiseRequestParams } from '../utilities/formatPackageRequest'
import { currencySymbol } from '../configuration/currencies'
import {
  clearGroupAvailabilityCache,
  fetchGroupAvailability
} from './fetchGroupAvailability'

//  see comment below
// import { allowedLandPackagesBrands } from '../configuration/brands'

export const FETCH_PACKAGES_BEGIN = 'FETCH_PACKAGES_BEGIN'
export const FETCH_PACKAGES_SUCCESS = 'FETCH_PACKAGES_SUCCESS'
export const FETCH_PACKAGES_FAILURE = 'FETCH_PACKAGES_FAILURE'
export const CLEAR_PACKAGE_DATA = 'CLEAR_PACKAGE_DATA'
export const fetchPackagesBegin = () => ({
  type: FETCH_PACKAGES_BEGIN
})

export const fetchPackagesSuccess = packages => ({
  type: FETCH_PACKAGES_SUCCESS,
  payload: packages
})

export const fetchPackagesError = error => ({
  type: FETCH_PACKAGES_FAILURE,
  payload: { error }
})

export const clearPackageData = () => ({ type: CLEAR_PACKAGE_DATA })

// Handle Availability Fetch & Functionality
export default function fetchPackages(ssoAgent) {
  return async (dispatch, getState) => {
    clearGroupAvailabilityCache()
    dispatch(fetchPackagesBegin())
    const state = getState()
    const activeSearchFilterData = getState().activeSearchFilterData

    /*
     * We are going to exclude Cruisetours if only searching Azamara.
     * This was requested by the client as they have incorrect, or old data.
     * We are also going to filter out any Cruisetour results return for Azamara if searching
     * more brands.
     * */

    const shouldFetchCruiseTours = Boolean(
      process.env.REACT_APP_INCLUDE_CRUISE_TOURS === 'true' &&
        activeSearchFilterData.brand !== 'Z'
    )

    let urlParams = formatVPSCruiseRequestParams(
      state,
      activeSearchFilterData,
      ssoAgent,
      shouldFetchCruiseTours
    )

    // Fetch Query string method
    const fetchQuery = new URLSearchParams()

    // Loop urlParams for undefined values & remove
    for (let key in urlParams) {
      if (
        typeof urlParams[key] === 'undefined' ||
        urlParams[key] === '' ||
        urlParams[key] === false
      ) {
        continue
      }

      if (key === 'duration') {
        fetchQuery.append('duration[match][]', urlParams[key])
      } else {
        fetchQuery.append(key, urlParams[key])
      }
    }

    const guestCount = activeSearchFilterData.guestCount
      ? Number(activeSearchFilterData.guestCount)
      : 2

    // Base API path
    const packagesBase = process.env.REACT_APP_FLOW_ENDPOINT_URL + 'vps-cruise?'

    // Formatted API path including base and params
    const packagesApi = packagesBase + fetchQuery.toString()

    try {
      const response = await axios.get(packagesApi)
      let availableCruises
      let availableCruiseTours

      if (
        response.data &&
        response.data['hydra:member'] &&
        response.data['hydra:member'][0] &&
        response.data['hydra:member'][0].packages
      ) {
        const packages = formatVPSPackages(
          response.data['hydra:member'][0].packages,
          guestCount,
          activeSearchFilterData.currency,
          currencySymbol[activeSearchFilterData.currency].symbol || '$',
          currencySymbol[activeSearchFilterData.currency].placement || 'start'
        )

        if (packages) {
          // Returns a separate entry for each package's pricing options,
          // which maps to a single table row per pricing option.
          availableCruises = packages.reduce((obj, packageData) => {
            if (packageData.justACruise) {
              const packageIdentifier = `${packageData.id}-${
                packageData.sailDate
              }-[${Number(urlParams.occupancy)}]`
              obj[packageIdentifier] = {
                ...packageData,
                // Identifies this specific package-pricing item
                identifier: packageIdentifier,
                // Identifies the package/date that this item belongs to
                packageIdentifier,
                stateKey: 'packagesData',
                pricingType: 'Individual',
                // Snapshot of the search criteria that produced this row, so
                // the quote submit can record the exact params used to quote
                // these prices even if the user later edits the search form.
                searchParameters: { ...urlParams }
              }

              // Create a separate row for each group using that group's pricing
              const groupIds = Object.values(packageData.pricing ?? {}).flatMap(
                ({ groups = {} }) => Object.keys(groups)
              )

              groupIds.forEach(groupId => {
                const groupPricingIdentifier = `${groupId}-${
                  packageData.startDate
                }-[${Number(urlParams.occupancy)}]`

                if (obj[groupPricingIdentifier]) return

                const newPricing = {
                  ...packageData.pricing
                }
                Object.entries(newPricing).forEach(([key, basePricingData]) => {
                  const groupPricingData = newPricing[key].groups[groupId]

                  if (!groupPricingData) {
                    delete newPricing[key]
                    return
                  }
                  // If we have pricing for this group, replace the relevant data with the group's data
                  newPricing[key] = {
                    ...basePricingData,
                    ...groupPricingData
                  }
                })

                // Recompute isSoldOut against the group's own pricing — the
                // base package can be flagged sold out when all individual
                // bestRates are null on a group-only sailing, but the group
                // row may still have valid prices.
                const groupHasPrice = Object.values(newPricing).some(
                  p => p?.bestGuestCountRate?.netPrice
                )

                obj[groupPricingIdentifier] = {
                  ...packageData,
                  isSoldOut:
                    !packageData.hasUnsupportedQualifiers && !groupHasPrice,
                  pricing: { ...newPricing },
                  pricingType: 'Group',
                  identifier: groupPricingIdentifier,
                  stateKey: 'packagesData',
                  groupId,
                  packageIdentifier,
                  searchParameters: { ...urlParams }
                }
              })
            }

            if (packageData.pricing) return obj
          }, {})

          availableCruiseTours = packages.reduce((obj, packageData) => {
            if (
              packageData.hasOwnProperty('justACruise') &&
              !packageData.justACruise &&
              packageData.brand !== 'Z' // remove Azamara
            ) {
              const key = `${packageData.id}-${packageData.sailDate}-[${Number(
                urlParams.occupancy
              )}]`
              obj[key] = {
                ...packageData,
                packageIdentifier: key,
                identifier: key,
                stateKey: 'cruiseToursData',
                searchParameters: { ...urlParams }
              }
            }
            return obj
          }, {})
        }

        // Fetch group profile/allocation data for any group-priced sailings.
        // fetchGroupAvailability caches by groupId+agencyId so this same data
        // is reused later when filtering pricing or selecting the package.
        try {
          const availableCruisesList = Object.entries(availableCruises)
          const groupRequests = availableCruisesList
            .filter(([, value]) => value.groupId)
            .map(([, value]) =>
              fetchGroupAvailability({
                groupId: value.groupId,
                agencyId: ssoAgent.agency.agency_id
              })
            )

          const groups = await Promise.all(groupRequests)
          // Multiple cruise entries can share a groupId (e.g. different sail
          // dates of the same package). Apply the group's data to all of them
          // so the per-sailing checkbox can decide upfront whether any cabins
          // are bookable for the group.
          groups.forEach(group => {
            const grpId = group?.grpProfileInfo?.groupId
            if (!grpId) return
            availableCruisesList
              .filter(([, value]) => value.groupId === grpId)
              .forEach(([cruiseKey]) => {
                availableCruises[cruiseKey].groupName =
                  group.grpProfileInfo.groupName
                availableCruises[cruiseKey].headquarterGroupFlag =
                  group.grpProfileInfo.headquarterGroupFlag
                availableCruises[cruiseKey].amenityOptionsAllocInfos =
                  group.amenityOptionsAllocInfos
                availableCruises[cruiseKey].categoryOptions =
                  group.categoryOptions
              })
          })
        } catch (error) {
          console.error(error)
        }

        // Sort rows within each sailing: Individual first, then Trad. Groups, then HQ Groups
        if (availableCruises) {
          const sailingEntries = new Map()
          Object.entries(availableCruises).forEach(([key, value]) => {
            const pkgId = value.packageIdentifier
            if (!sailingEntries.has(pkgId)) sailingEntries.set(pkgId, [])
            sailingEntries.get(pkgId).push([key, value])
          })

          availableCruises = {}
          sailingEntries.forEach(entries => {
            entries
              .sort(([, a], [, b]) => {
                if (a.pricingType === 'Individual') return -1
                if (b.pricingType === 'Individual') return 1
                const aHQ = a.headquarterGroupFlag === 'Yes'
                const bHQ = b.headquarterGroupFlag === 'Yes'
                if (aHQ === bHQ) return 0
                return aHQ ? 1 : -1
              })
              .forEach(([key, value]) => {
                availableCruises[key] = value
              })
          })
        }

        if (
          response.data['hydra:member'][0].prePost &&
          response.data['hydra:member'][0].prePost.landPackages
        ) {
          const landPackages = formatLandPackages(
            response.data['hydra:member'][0].prePost.landPackages
          )
          dispatch(setLandPackages(landPackages))
        }

        // set pagination values
        if (response.data['hydra:member'][0].pagination) {
          dispatch(
            setActivePagination(response.data['hydra:member'][0].pagination)
          )
        }
      }

      if (availableCruises) {
        dispatch(fetchPackagesSuccess(availableCruises))
        // Dispatch GA event for cruises
        triggerCustomGaEvent('equoteTracking-cruisesRequest', availableCruises)
      }

      if (availableCruiseTours) {
        dispatch(setCruiseTours(availableCruiseTours))
        // Dispatch GA event for cruises
        triggerCustomGaEvent(
          'equoteTracking-cruiseToursRequest',
          availableCruiseTours
        )
      }

      return
    } catch (error) {
      dispatch(fetchPackagesError(error))
    }
  }
}
